/**
 * Video popout — the one genuinely new piece carried across from the 2025
 * RecitalSongs trial (card G27X4s3EHXIIg1trqvEF, `RecitalSongs.js:152-223`).
 *
 * WHAT IT IS FOR: a chrome-stripped window holding nothing but the video, so a
 * teacher can drag it onto an external monitor and the student reads along
 * while singing. That is why it is a real window and not an in-page player —
 * the whole value is that it leaves the lesson surface behind.
 *
 * THREE DELIBERATE DEPARTURES FROM THE TRIAL, all of them fixes:
 *
 * 1. **A FIXED window name, not the song title.** The trial named the window
 *    after the song, so every song opened a NEW window and the teacher
 *    re-dragged it to the second monitor each time. One constant name means the
 *    browser reuses the same physical window: park it once, and each new song
 *    replaces its contents in place.
 *
 * 2. **The title never touches HTML and the video id is VALIDATED.** The trial
 *    interpolated `${title}` into `document.write` raw, twice, in a window that
 *    inherited our origin; titles arrive from three third-party music APIs.
 *    Since Lesson Dashboard pass 3 release 5 the window is the /stage PAGE and
 *    the video travels to it as DATA over the lesson's BroadcastChannel
 *    (stage/lessonStage.js), rendered by React. The id is still checked here
 *    against YouTube's own alphabet, and again by the stage, because it lands
 *    in an iframe src and a whitelist beats an escape there.
 *
 * 3. **It RETURNS false instead of calling `alert()`.** A blocked popup is a
 *    normal, recoverable state that the calling surface should render in its own
 *    voice; `alert` appears nowhere else in the portal.
 *
 * The non-YouTube fallback is restricted to http(s) so a stored `javascript:`
 * URL can never be handed to `window.open`.
 */

import { openStageVideo } from './stage/lessonStage';

/** YouTube ids are 11 chars of [A-Za-z0-9_-]; the range is loosened slightly
 *  rather than pinned at 11 in case the id length ever moves, but the ALPHABET
 *  is the part doing the security work. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{6,20}$/;

/* One reused window, named in stage/lessonStage.js (STAGE_WINDOW_NAME =
   'cyvLessonVideo'): park it on the second monitor once and every video, and
   every track's scrolling words, replace what is on it (departure 1 above). */

const isHttpUrl = (url) => /^https?:\/\//i.test(String(url || '').trim());

/**
 * The four YouTube URL shapes a teacher actually pastes: watch?v=, youtu.be/,
 * /embed/ and /shorts/. Returns null for anything that is not one of them —
 * including a well-formed YouTube URL whose id fails the alphabet check.
 *
 * `/` is excluded from the captured run (the trial allowed it), so
 * `youtu.be/abc/extra` yields `abc` rather than `abc/extra`.
 */
export const getYoutubeVideoId = (url) => {
	if (!url) return null;
	const patterns = [
		/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?/]+)/,
		/youtube\.com\/shorts\/([^&\s?/]+)/,
	];
	for (const pattern of patterns) {
		const match = String(url).match(pattern);
		if (match && YOUTUBE_ID.test(match[1])) return match[1];
	}
	return null;
};

/**
 * Open the video in the shared popout window.
 *
 * @returns {boolean} false when the popup was blocked, or when a non-YouTube
 *   URL is not http(s). The caller renders that; this never alerts.
 */
export const openVideoWindow = (url, title) => {
	const videoId = getYoutubeVideoId(url);

	// Not a YouTube URL we can embed — hand the raw link to the browser rather
	// than failing. `noopener` because this one we do not control.
	if (!videoId) {
		if (!isHttpUrl(url)) return false;
		return Boolean(window.open(url, '_blank', 'noopener,width=2560,height=1440'));
	}

	// The window is the lesson's STAGE now (stage/lessonStage.js): one page
	// with a video mode and a scrolling-lyrics mode, written once. The title
	// and id go in as DATA (textContent, a validated src), never into HTML,
	// which is what departure 2 above protects against, now by construction.
	return openStageVideo(videoId, title || 'Video');
};
